import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ProfileService } from '../profile.service';
import { User } from '../../../shared/model/index';
import { SharedModule } from '../../../shared/shared.module';
import { AlertService } from 'app/core-lib/services/alerts/alert.service';
import { AuthenticationService } from 'app/_services';
import { UtilService } from '../../../shared/util.service';
import { SessionService } from '../../../_services/session.service';

@Component({
  selector: 'mh-profile-superadmin-properties',
  imports: [CommonModule, SharedModule],
  providers: [ProfileService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="tw-font-[Rubik] tw-bg-white tw-shadow-lg tw-rounded-lg tw-w-full">
      <mh-box-element
        class="tw-px-6 tw-py-6 tw-block tw-w-full"
        [title]="'configuration.profile.super_admin.title' | translate"
      >
        @if (demoModeResource.isLoading()) {
          <div class="tw-flex tw-justify-center tw-items-center tw-min-h-[120px]">
            <mh-spinner-load [size]="60"></mh-spinner-load>
          </div>
        } @else {
          <div class="tw-flex tw-flex-col tw-gap-6 tw-items-center">
            <div class="tw-flex tw-flex-col tw-gap-3 tw-w-full">
              <label class="tw-text-sm tw-font-bold tw-text-black">{{
                'configuration.profile.super_admin.demo_label' | translate
              }}</label>
              <div class="tw-flex tw-items-center tw-gap-2">
                <mh-core-lib-toggle
                  [value]="demoMode()"
                  [label]="
                    demoMode()
                      ? 'concierge.config.service_status.activated'
                      : 'concierge.config.service_status.deactivated'
                  "
                  [labelPosition]="'horizontal'"
                  [isDisabled]="saving()"
                  (onClick)="onDemoToggle($event)"
                ></mh-core-lib-toggle>
              </div>
            </div>
          </div>
        }
      </mh-box-element>
    </div>
  `,
})
export class SuperAdminPropertiesComponent {
  readonly currentProfile = input.required<User>();

  readonly demoMode = signal(false);
  readonly saving = signal(false);

  private readonly profileService = inject(ProfileService);
  private readonly demoModeUserId = computed(() => this.currentProfile().id);
  readonly demoModeResource = this.profileService.getDemoModeResource(this.demoModeUserId);
  private readonly destroyRef = inject(DestroyRef);
  private readonly alertService = inject(AlertService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly utilService = inject(UtilService);
  private readonly sessionService = inject(SessionService);

  constructor() {
    effect(() => {
      const value = this.demoModeResource.value();
      if (value != null) this.demoMode.set(!!value.demo_mode);
      if (this.demoModeResource.error()) {
        this.alertService.handleAlert(
          'user_management.actions.error',
          5000,
          'bottom',
          'end',
          'snackbar-panel-error-user-config',
        );
      }
    });
  }

  onDemoToggle(event: { id: number; value: boolean; name: string }): void {
    const newValue = event.value;
    this.demoMode.set(newValue);
    this.updateDemoModeOnServer(newValue);
  }

  private updateDemoModeOnServer(demoMode: boolean): void {
    const profile = this.currentProfile();
    if (!profile?.isSuperAdmin()) return;
    this.saving.set(true);
    this.profileService
      .updateDemoMode(profile.id, demoMode)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.demoModeResource.reload();
          const jwt = this.authenticationService.getJWT();
          const userId = profile.id;
          if (jwt?.refreshToken) {
            this.authenticationService
              .refreshToken(jwt)
              .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(() =>
                  this.authenticationService.profile(userId).pipe(
                    switchMap((profileData: Record<string, unknown>) => {
                      const updatedUser = User.createFromApi(profileData);
                      return of(updatedUser);
                    }),
                    map((updatedUser: User) => {
                      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
                      this.utilService.currentUser = updatedUser;
                      this.sessionService.currentUser = updatedUser;
                      return updatedUser;
                    }),
                  ),
                ),
              )
              .subscribe({
                next: () => window.location.reload(),
              });
          }
          this.alertService.handleAlert(
            'models.user.data_saved_correctly',
            5000,
            'bottom',
            'end',
            'snackbar-panel-success-user-config',
          );
        },
        error: () => {
          this.saving.set(false);
          this.demoMode.set(!demoMode);
          this.alertService.handleAlert(
            'user_management.actions.error',
            5000,
            'bottom',
            'end',
            'snackbar-panel-error-user-config',
          );
        },
      });
  }
}
